import BuyersConversationM from '@/buyers/modules/Conversation'
import InboxCommon, { Conversation } from '@/gf/components/Inbox'
import ConversationM from '@/gf/modules/Conversation'
import {
  ConversationRowContent,
  ConversationRowParty,
  NewConversation,
  Pagination,
  User,
} from '../../types'
import {
  FetchConversationsDocument,
  FetchMessagesForConversationDocument,
  useCreateConversationMutation,
  useCreateMessageMutation,
  useFetchMessagesForConversationQuery,
  useReadReceiptsForConversationQuery,
} from '../_gen/gql'
import useGqlClient from '../hooks/useGqlClient'
import SuppliersConversationM from '../modules/Conversation'
import { StoreOrder } from '../types'
import InboxDetails from './InboxDetails'

const conversationRowContent =
  (storeOrder: Omit<StoreOrder, 'payOnInvoice'>) =>
  (conversation: Conversation | NewConversation): ConversationRowContent => {
    const { store, admin, name } = conversation
    let title: ConversationRowParty
    let description: ConversationRowParty
    if (!admin) {
      title = store
        ? {
            type: 'supplier',
            name: store ? BuyersConversationM.conversationName(store) : 'Error - undefined Store',
          }
        : {
            type: 'internal_org',
            name: '(Internal)',
          }
      description = {
        type: 'buyer',
        name: SuppliersConversationM.conversationNameForStoreOrder(storeOrder, true),
      }
    } else {
      title = { type: 'admin', name }
      description = store
        ? { type: 'supplier', name: BuyersConversationM.conversationName(store) }
        : {
            type: 'buyer',
            name: SuppliersConversationM.conversationNameForStoreOrder(storeOrder, true),
          }
    }
    return { name: title, details: description }
  }

const AdminStoreOrderInbox = ({
  conversations,
  refetchConversations,
  page,
  setPage,
  pagination,
  user,
  storeOrder,
  selectedConversationId,
  onSelectedConversationIdChange,
}: {
  conversations: Conversation[]
  refetchConversations: () => Promise<unknown>
  page: number
  setPage: (page: number) => void
  pagination: Pagination
  user: Pick<User, 'id' | 'role'>
  storeOrder: Omit<StoreOrder, 'payOnInvoice'>
  selectedConversationId: string | undefined
  onSelectedConversationIdChange: (conversationId: string | undefined) => void
}) => {
  const gqlClient = useGqlClient()
  const [createMessage] = useCreateMessageMutation({
    client: gqlClient,
    refetchQueries: [FetchMessagesForConversationDocument, FetchConversationsDocument],
  })
  const [createConversation] = useCreateConversationMutation({
    client: gqlClient,
    refetchQueries: [FetchConversationsDocument],
  })

  const newConversationsUnfiltered = [
    // Admin conversations
    storeOrder.order.requestForQuote
      ? BuyersConversationM.newAdminConversationRfq(
          storeOrder.order.requestForQuote.user?.organization?.id,
          storeOrder.order.requestForQuote
        )
      : BuyersConversationM.newAdminConversationStoreOrder(storeOrder),
    storeOrder.order.requestForQuote
      ? SuppliersConversationM.newAdminConversationRfq(
          storeOrder.order.requestForQuote,
          storeOrder.store
        )
      : SuppliersConversationM.newAdminConversationStoreOrder(storeOrder),
    // Non-Admin conversation
    storeOrder.order.requestForQuote
      ? SuppliersConversationM.newNonAdminConversationRfq(
          storeOrder.order.requestForQuote,
          storeOrder.store,
          true
        )
      : SuppliersConversationM.newNonAdminConversationStoreOrder(storeOrder, true),
  ]
  // Filter out existing conversations
  const newConversations = ConversationM.filterNewConversations(
    newConversationsUnfiltered,
    conversations
  )

  const selectedConversation =
    conversations &&
    [...conversations, ...newConversations].find((c) => c.id === selectedConversationId)

  const { error: messagesError, ...messagesQueryResult } = useFetchMessagesForConversationQuery({
    client: gqlClient,
    variables: {
      conversationId: selectedConversation?.id as string,
      vendorId: null,
      vendorUserId: null,
    },
    pollInterval: ConversationM.MESSAGES_POLL_INTERVAL,
    // Only refetch if the conversation is unread, so we can fetch the correct "read" status
    onCompleted: selectedConversation?.unreadMessages ? refetchConversations : undefined,
    skip:
      !selectedConversationId || !selectedConversation || 'newConversation' in selectedConversation,
  })

  const { fetchMessagesForConversation: messages } = messagesQueryResult.data || {}

  const { readReceiptsForConversation } =
    useReadReceiptsForConversationQuery({
      client: gqlClient,
      variables: {
        conversationId: selectedConversation?.id as string,
        vendorId: null,
        vendorUserId: null,
      },
      pollInterval: ConversationM.MESSAGES_POLL_INTERVAL,
      skip:
        !selectedConversationId ||
        !selectedConversation ||
        'newConversation' in selectedConversation,
    }).data || {}

  return (
    <InboxCommon
      className="mt-4"
      user={user}
      conversations={conversations}
      selectedConversation={selectedConversation}
      selectedConversationId={selectedConversationId}
      messages={messages}
      messagesError={messagesError}
      readReceiptsForConversation={readReceiptsForConversation}
      onSelectedConversationIdChange={onSelectedConversationIdChange}
      page={page}
      setPage={setPage}
      pagination={pagination}
      newConversations={newConversations}
      DetailsForConversation={InboxDetails.StoreOrder(storeOrder)}
      conversationRowContent={conversationRowContent(storeOrder)}
      createMessage={(variables) =>
        createMessage({ variables: { ...variables, vendorId: null, vendorUserId: null } })
      }
      createConversation={(variables) =>
        createConversation({
          variables: { ...variables, vendorId: null, vendorUserId: null },
        }).then((resp) => resp.data?.createConversation)
      }
      readOnly={
        selectedConversation && ConversationM.isInternalOrgConversation(selectedConversation)
      }
    />
  )
}

export default AdminStoreOrderInbox
